import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, FormControlLabel, FormGroup, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useContent } from "../../logic";
import { useLocation, useParams } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import { PatternFormat } from "react-number-format";
import { useOrganization } from '../../components/OrganizationProvider';
import { useGetOrder } from "../../logic/useApiQuery";
import { useUpdateSmsPreferenceStatus } from "../../logic/useApiMutation";
import { useOptimisticUpdatePortalOrder } from "../../logic/optimisticUpdates";
import { isAdhocPortalOrder } from "../../utils/isAdhocPortalOrder";
import { phoneNumberPattern } from "../../utils/phoneNumberUtils";
import { parsePhoneNumber } from "../../utils/parsePhoneNumber";

type PhoneError = 'invalid' | 'mismatch' | null
const PHONE_NUMBER_EXAMPLE = '555-123-4567'
const INVALID_PHONE_ERROR_TEXT = `Please enter a valid phone number, like ${PHONE_NUMBER_EXAMPLE}`

export function SmsPreferenceStatusDialog({ open, setOpen, back }: {
  open: boolean;
  setOpen: Function;
  back?: Function;
}) {
  const { closingId } = useParams()
  const { search } = useLocation()
  const { data: globalOrderData } = useGetOrder(closingId ?? '', search ?? '')
  const { ClosingsText } = useContent()
  const { CompanyDisplayName } = useOrganization()
  const [phoneError, setPhoneError] = useState<PhoneError>(null)
  const [unsubscribeExistingConsent, setUnsubscribeExistingConsent] = useState(false)

  const initialPhoneNumber = (globalOrderData && !isAdhocPortalOrder(globalOrderData.Order) && globalOrderData?.Order.SmsPreferenceStatus?.phoneNumber) || ''
  const initialConsent = (globalOrderData && !isAdhocPortalOrder(globalOrderData.Order) && globalOrderData?.Order.SmsPreferenceStatus?.consent) ?? false
  const hasExistingSmsConsent = initialConsent && !!initialPhoneNumber

  useEffect(() => {
    if (!open) return

    setPhoneError(null)
    setUnsubscribeExistingConsent(false)
  }, [open])

  const { optimisticUpdatePortalOrder } = useOptimisticUpdatePortalOrder()

  const { isLoading: loading, error: updateSmsPreferenceStatusError, mutate } = useUpdateSmsPreferenceStatus(search, {
    onSuccess: (data, variables) => {
      const phoneNumber = 'phoneNumber' in variables ? variables.phoneNumber : initialPhoneNumber
      optimisticUpdatePortalOrder({
        orderKey: closingId ?? '',
        search,
        patch: {
          SmsPreferenceStatus: {
            consent: variables.consent,
            phoneNumber: parsePhoneNumber(phoneNumber).redacted,
            matchingPhoneNumber: data.matchingPhoneNumber
          }
        }
      });
      if (!data.matchingPhoneNumber && back) {
        back()
      } else {
        setOpen(false)
      }
    }
  })

  if (!globalOrderData) { return null }
  if (isAdhocPortalOrder(globalOrderData.Order)) { return null }

  const handleSave = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setPhoneError(null)

    const formData = new FormData(event.currentTarget)
    const consent = hasExistingSmsConsent
      ? !unsubscribeExistingConsent
      : formData.get('consentCheckbox') === 'on'
    const phoneNumber = ((formData.get('phoneNumber') as string) || '').trim()

    if (phoneNumber && !phoneNumberPattern.test(phoneNumber)) {
      setPhoneError('invalid')
      return
    }

    mutate(phoneNumber
      ? { phoneNumber, consent, orderKey: closingId || '' }
      : { consent, orderKey: closingId || '' })
  }

  return (
    <Dialog
      key="userCancel"
      open={open}
      onClose={() => setOpen(false)}
    >
      <form onSubmit={handleSave}>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }} variant='h6'>
            SMS Settings
          </DialogContentText>
          { !hasExistingSmsConsent && <Typography>
            You have not yet provided consent to receive SMS updates about your transaction.
          </Typography> }
          <FormGroup>
            <PatternFormat
              customInput={TextField}
              name="phoneNumber"
              label="Phone Number"
              margin="normal"
              fullWidth
              format="###-###-####"
              placeholder={PHONE_NUMBER_EXAMPLE}
              helperText={`Example: ${PHONE_NUMBER_EXAMPLE}`}
              inputProps={{ inputMode: 'numeric' }}
            />
            <FormControlLabel
              control={<Checkbox name="consentCheckbox" defaultChecked={initialConsent} />}
              label={`I consent to receive SMS notifications & alerts about my ${ClosingsText} from ${CompanyDisplayName}.`}
            />
            <Typography variant="body2" sx={{ mt: 2, ml: 4, display: 'block', color: 'text.secondary' }}>
              Message frequency varies. Message & data rates may apply. You can reply STOP to unsubscribe at any time.
            </Typography>
          </FormGroup>
        </DialogContent>
        {phoneError && (
          <DialogContent>
            <DialogContentText variant="body1" color="error">
              {phoneError === 'invalid' && INVALID_PHONE_ERROR_TEXT}
            </DialogContentText>
          </DialogContent>
        )}
        {updateSmsPreferenceStatusError && (
          <DialogContent>
            <DialogContentText variant="body1" color="error">
              {updateSmsPreferenceStatusError.userErrorMessage}
            </DialogContentText>
          </DialogContent>
        )}
        <DialogActions>
          <Button type="button" variant="rounded" onClick={() => setOpen(false)}>Close</Button>
          {back && <Button type="button" variant="rounded" onClick={() => back()}>Back</Button>}
          <LoadingButton loading={loading} type="submit" variant="contained">Save</LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  )
}
